import axios from "axios";
import AuthService from "../utils/AuthService";

const ensureTrailingSlash = (value) => {
  if (!value) {
    return "";
  }
  return value.endsWith("/") ? value : `${value}/`;
};

const ensureNoTrailingSlash = (value) => {
  if (!value) {
    return "";
  }
  return value.replace(/\/+$/, "");
};

const getGatewayBaseUrl = () => {
  const raw = process.env.REACT_APP_API_GATEWAY_URL;

  if (!raw) {
    throw new Error("API gateway URL is not configured.");
  }

  return ensureTrailingSlash(raw.trim());
};

const getItemsBaseUrl = () => {
  const raw = process.env.REACT_APP_ITEMS_API_URL;

  if (!raw) {
    throw new Error("Items API URL is not configured.");
  }

  return ensureNoTrailingSlash(raw.trim());
};

const withAuthorization = (config = {}) => {
  const authConfig = AuthService.getAuthHeader() || {};
  const authHeaders = authConfig.headers || {};
  const configHeaders = config.headers || {};

  return {
    ...authConfig,
    ...config,
    headers: {
      ...configHeaders,
      ...authHeaders,
    },
  };
};

const buildGatewayUrl = (path = "") => {
  const baseUrl = getGatewayBaseUrl();
  const sanitizedBase = baseUrl.replace(/\/+$/, "");
  const sanitizedPath = path.replace(/^\/+/, "");

  if (!sanitizedPath) {
    return sanitizedBase;
  }

  return `${sanitizedBase}/${sanitizedPath}`;
};

const LessonAuthoringService = {
  async fetchCourses() {
    const url = buildGatewayUrl("app/v1/courses");
    const response = await axios.get(url, AuthService.getAuthHeader());
    return response.data || [];
  },

  async createCourse(payload) {
    const baseUrl = getItemsBaseUrl();
    const url = `${baseUrl}/course`;
    const response = await axios.post(
      url,
      payload,
      AuthService.getAuthHeader()
    );
    return response.data;
  },

  async updateCourse(courseId, payload) {
    if (courseId === undefined || courseId === null) {
      throw new Error("A course id is required to update a course.");
    }

    const baseUrl = getItemsBaseUrl();
    const url = `${baseUrl}/course/${encodeURIComponent(courseId)}`;
    const response = await axios.put(url, payload, AuthService.getAuthHeader());
    return response.data;
  },

  async fetchModules(courseId) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to fetch modules.");
    }

    const url = buildGatewayUrl(
      `app/v1/course/${encodeURIComponent(courseId)}/modules`
    );
    const response = await axios.get(url, AuthService.getAuthHeader());
    return response.data || [];
  },

  async createModule(payload) {
    const baseUrl = getItemsBaseUrl();
    const url = `${baseUrl}/course/module`;
    const response = await axios.post(
      url,
      payload,
      AuthService.getAuthHeader()
    );
    return response.data;
  },

  async updateModule(courseId, moduleId, payload) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to update a module.");
    }

    if (!moduleId && moduleId !== 0) {
      throw new Error("A module id is required to update a module.");
    }

    const baseUrl = getItemsBaseUrl();
    const url = `${baseUrl}/course/${encodeURIComponent(
      courseId
    )}/module/${encodeURIComponent(moduleId)}`;
    const response = await axios.put(url, payload, AuthService.getAuthHeader());
    return response.data;
  },

  async fetchLessons(courseId, moduleId) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to fetch lessons.");
    }

    if (!moduleId && moduleId !== 0) {
      throw new Error("A module id is required to fetch lessons.");
    }

    const url = buildGatewayUrl(
      `app/v1/course/${encodeURIComponent(
        courseId
      )}/module/${encodeURIComponent(moduleId)}/lessons`
    );
    const response = await axios.get(url, AuthService.getAuthHeader());
    return response.data || [];
  },

  async fetchLessonContent(
    courseId,
    moduleId,
    chapterId,
    lessonId,
    pageNumber
  ) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to fetch lesson content.");
    }

    if (!moduleId && moduleId !== 0) {
      throw new Error("A module id is required to fetch lesson content.");
    }

    if (!lessonId && lessonId !== 0) {
      throw new Error("A lesson id is required to fetch lesson content.");
    }

    const query = pageNumber ? `?pgnum=${encodeURIComponent(pageNumber)}` : "";
    const url = buildGatewayUrl(
      `app/v1/coursecontent/${encodeURIComponent(
        courseId
      )}/module/${encodeURIComponent(moduleId)}/chapter/${encodeURIComponent(
        chapterId
      )}/lesson/${encodeURIComponent(lessonId)}${query}`
    );
    const response = await axios.get(url, AuthService.getAuthHeader());
    return response.data;
  },

  async searchCourseContent(courseId, query, moduleId, nextToken = null) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to search course content.");
    }

    const trimmedQuery = typeof query === "string" ? query.trim() : "";
    if (!trimmedQuery) {
      throw new Error("A search query is required to search course content.");
    }

    const baseUrl = getItemsBaseUrl();
    const params = new URLSearchParams();

    params.set("q", trimmedQuery);
    params.set("courseid", String(courseId));

    if (moduleId !== undefined && moduleId !== null && moduleId !== "") {
      params.set("moduleid", String(moduleId));
    }

    // ✅ ADD THIS
    if (nextToken) {
      params.set("nextToken", nextToken);
    }

    const url = `${baseUrl}/search?${params.toString()}`;
    const response = await axios.get(url, AuthService.getAuthHeader());
    return response.data;
  },

  async createLesson(courseId, moduleId, chapterId, payload) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to create a lesson.");
    }

    if (!moduleId && moduleId !== 0) {
      throw new Error("A module id is required to create a lesson.");
    }

    const url = buildGatewayUrl(
      `app/v1/courselesson/${encodeURIComponent(
        courseId
      )}/module/${encodeURIComponent(moduleId)}/chapter/${encodeURIComponent(
        chapterId
      )}`
    );
    const response = await axios.put(url, payload, AuthService.getAuthHeader());
    return response.data;
  },

  async updateLessonContent(
    courseId,
    moduleId,
    chapterId,
    lessonId,
    payload = {},
    pageNumber = 1
  ) {
    if (!courseId && courseId !== 0) {
      throw new Error("A course id is required to update lesson content.");
    }

    if (!moduleId && moduleId !== 0) {
      throw new Error("A module id is required to update lesson content.");
    }

    if (!lessonId && lessonId !== 0) {
      throw new Error("A lesson id is required to update lesson content.");
    }

    const baseUrl = getItemsBaseUrl();
    const normalizedLessonId =
      payload?.lessonId ?? payload?.id ?? payload?.Id ?? lessonId;
    const normalizedChapterId =
      payload?.chapterId ?? payload?.ChapterId ?? chapterId;
    const requestPayload = {
      ...payload,
      courseId:
        payload?.courseId ?? payload?.CourseId ?? String(courseId ?? ""),
      moduleId:
        payload?.moduleId ?? payload?.ModuleId ?? String(moduleId ?? ""),
      chapterId:
        normalizedChapterId === undefined || normalizedChapterId === null
          ? undefined
          : String(normalizedChapterId),
      lessonId:
        normalizedLessonId === undefined || normalizedLessonId === null
          ? undefined
          : String(normalizedLessonId),
      id:
        payload?.id ??
        payload?.Id ??
        (normalizedLessonId === undefined || normalizedLessonId === null
          ? undefined
          : String(normalizedLessonId)),
      pageNumber:
        payload?.pageNumber ??
        (pageNumber === undefined || pageNumber === null
          ? undefined
          : Number(pageNumber)),
    };

    if (!requestPayload.lessonId) {
      throw new Error("A lesson id is required to update lesson content.");
    }

    const url = `${baseUrl}/lesson`;
    const response = await axios.put(url, requestPayload, withAuthorization());
    return response.data;
  },

  async rearrangeLesson(payload) {
    const baseUrl = getItemsBaseUrl();
    const url = `${baseUrl}/lesson/rearrange`;
    const response = await axios.post(url, payload, withAuthorization());
    return response.data;
  },
};

export default LessonAuthoringService;
